import React, { useState } from 'react';
import { Mail, MessageCircle, Shield, BookOpen } from 'lucide-react';

const ContactView = () => {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = () => {
    if (!form.name || !form.email || !form.message) return;
    const mailto = `mailto:hello@nutrius.co?subject=${encodeURIComponent(form.subject || 'Contact from Nutrius Web')}&body=${encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`)}`;
    window.location.href = mailto;
    setSubmitted(true);
  };

  const CONTACT_OPTIONS = [
    { icon: Mail,          title: 'General',         email: 'hello@nutrius.co',    desc: 'General questions and feedback' },
    { icon: Shield,        title: 'Privacy',         email: 'privacy@nutrius.co',  desc: 'Privacy policy and data requests' },
    { icon: Shield,        title: 'Security',        email: 'security@nutrius.co', desc: 'Report a security vulnerability' },
    { icon: BookOpen,      title: 'Press',           email: 'hello@nutrius.co',    desc: 'Media and press inquiries' },
  ];

  return (
    <div className="max-w-4xl mx-auto">

      {/* Header */}
      <div className="mb-xxxl">
        <h1 className="text-display font-rounded text-primary mb-md">Contact</h1>
        <p className="text-title-3 text-text-secondary max-w-xl">
          We are a small team and we read every message. Reach out with questions, feedback, or anything on your mind.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-xl">

        {/* Left — form */}
        <div className="lg:col-span-2">
          {submitted ? (
            <div
              className="p-xl rounded-large text-center"
              style={{ border: '1px solid rgba(var(--color-primary),0.15)', background: 'rgba(var(--color-primary),0.04)' }}
            >
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-md">
                <Mail size={22} className="text-primary" />
              </div>
              <h2 className="text-title-2 font-rounded text-primary mb-sm">Message Sent</h2>
              <p className="text-body text-text-secondary">
                Your email client should have opened. If it didn't, email us directly at{' '}
                <a href="mailto:hello@nutrius.co" className="text-primary hover:underline">hello@nutrius.co</a>.
              </p>
            </div>
          ) : (
            <div
              className="p-xl rounded-large bg-surface"
              style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
            >
              <h2 className="text-title-3 font-rounded text-primary mb-lg">Send a Message</h2>

              <div className="space-y-md">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-md">
                  <div>
                    <label className="text-caption font-semibold text-text-primary mb-xs block">Name</label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className="w-full px-md py-sm bg-background border border-text-tertiary/20 rounded-medium text-body focus:border-primary focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="text-caption font-semibold text-text-primary mb-xs block">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="your@email.com"
                      className="w-full px-md py-sm bg-background border border-text-tertiary/20 rounded-medium text-body focus:border-primary focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-caption font-semibold text-text-primary mb-xs block">Subject</label>
                  <input
                    type="text"
                    name="subject"
                    value={form.subject}
                    onChange={handleChange}
                    placeholder="What is this about?"
                    className="w-full px-md py-sm bg-background border border-text-tertiary/20 rounded-medium text-body focus:border-primary focus:outline-none"
                  />
                </div>

                <div>
                  <label className="text-caption font-semibold text-text-primary mb-xs block">Message</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    placeholder="Tell us what's on your mind..."
                    rows={6}
                    className="w-full px-md py-sm bg-background border border-text-tertiary/20 rounded-medium text-body focus:border-primary focus:outline-none resize-none"
                  />
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={!form.name || !form.email || !form.message}
                  className="w-full py-md bg-primary text-white rounded-large text-callout font-semibold hover:bg-primary/90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Send Message
                </button>

                <p className="text-xs text-text-tertiary text-center">
                  This opens your email client. Alternatively email us directly at{' '}
                  <a href="mailto:hello@nutrius.co" className="text-primary hover:underline">hello@nutrius.co</a>
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Right — contact options */}
        <div className="space-y-md">
          <div
            className="p-lg rounded-large bg-surface"
            style={{ border: '1px solid rgba(var(--color-text-tertiary),0.1)' }}
          >
            <h3 className="text-callout font-semibold text-text-primary mb-md">Direct Contact</h3>
            <div className="space-y-md">
              {CONTACT_OPTIONS.map(({ icon: Icon, title, email, desc }) => (
                <a
                  key={title}
                  href={`mailto:${email}`}
                  className="flex items-start gap-sm group"
                >
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-xs">
                    <Icon size={13} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-caption font-semibold text-text-primary group-hover:text-primary transition-colors">{title}</p>
                    <p className="text-xs text-text-secondary">{desc}</p>
                    <p className="text-xs text-primary">{email}</p>
                  </div>
                </a>
              ))}
            </div>
          </div>

          <div
            className="p-lg rounded-large"
            style={{ background: 'rgba(var(--color-primary),0.04)', border: '1px solid rgba(var(--color-primary),0.12)' }}
          >
            <div className="flex items-center gap-sm mb-sm">
              <MessageCircle size={16} className="text-primary" />
              <p className="text-caption font-semibold text-primary">Response Time</p>
            </div>
            <p className="text-xs text-text-secondary">
              We aim to respond to all messages within 1–2 business days. For urgent security issues, we respond within 24 hours.
            </p>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ContactView;
